import { AfterViewInit, ChangeDetectorRef, Component, input, OnDestroy, SimpleChanges } from '@angular/core';
import { GraphData } from 'src/app/bo/blastModel/fragmentation';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BlastService } from 'src/app/services/blast.service';
import { Chart } from 'src/app/utils/chart-config';
import { MatDialog } from '@angular/material/dialog';
import { ImageModalComponent } from './image-modal.component';
import { TranslateService } from '@ngx-translate/core';

const CHART_COLORS = {
  amarante: 'rgba(126, 0, 63, 0.8)',
  amaranteLight: 'rgba(126, 0, 63, 0.2)',
};

@Component({
  selector: 'app-fragmentation',
  templateUrl: './fragmentation.component.html',
  styleUrls: ['./fragmentation.component.scss'],
  standalone: false
})
export class FragmentationComponent implements AfterViewInit, OnDestroy {
  blastId = input.required<number>();
  imagePath: SafeUrl | null = null;
  private currentImageUrl: string | null = null;
  private chart: Chart | null = null;

  // Section collapse state
  isFragmentationCollapsed = false;

  // Chart visibility (both can be enabled at the same time)
  showDistribution = true;
  showCumulative = true;

  // Data
  data: GraphData | null = null;

  // Interval inputs
  intervalMin = 0;
  intervalMax = 340;
  intervalPercentage = 0;

  // Diameter size inputs (customizable percentages)
  percentD1 = 20;
  percentD2 = 50;
  percentD3 = 80;
  diameterD1 = 0;
  diameterD2 = 0;
  diameterD3 = 0;

  constructor(
    private readonly blastService: BlastService,
    private readonly sanitizer: DomSanitizer,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    if (this.blastId()) {
      this.initChart();
      this.loadMuckpileImage();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['blastId'] && !changes['blastId'].firstChange) {
      this.resetValues();
      this.initChart();
      this.loadMuckpileImage();
    }
  }

  private resetValues(): void {
    this.percentD1 = 20;
    this.percentD2 = 50;
    this.percentD3 = 80;
    this.diameterD1 = 0;
    this.diameterD2 = 0;
    this.diameterD3 = 0;
    this.intervalPercentage = 0;
    this.data = null;
  }

  ngOnDestroy(): void {
    if (this.currentImageUrl) {
      window.URL.revokeObjectURL(this.currentImageUrl);
    }
    if (this.chart) {
      this.chart.destroy();
    }
  }

  // Section toggle methods
  toggleFragmentationSection(): void {
    this.isFragmentationCollapsed = !this.isFragmentationCollapsed;
    if (this.isFragmentationCollapsed) {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    } else {
      setTimeout(() => this.initChart(), 0);
    }
  }

  // Toggle chart dataset visibility
  toggleDistribution(): void {
    this.showDistribution = !this.showDistribution;
    this.updateChartVisibility();
  }

  toggleCumulative(): void {
    this.showCumulative = !this.showCumulative;
    this.updateChartVisibility();
  }

  private updateChartVisibility(): void {
    if (!this.chart) return;

    this.chart.getDatasetMeta(0).hidden = !this.showDistribution;
    this.chart.getDatasetMeta(1).hidden = !this.showCumulative;

    if (this.chart.options.scales) {
      const yRight = this.chart.options.scales['yRight'] as any;
      const yLeft = this.chart.options.scales['yLeft'] as any;

      if (yRight) {
        yRight.display = this.showDistribution;
        if (this.showDistribution && this.data?.percent && this.data.percent.length > 0) {
          const maxDistribution = Math.max(...this.data.percent);
          yRight.max = Math.ceil(maxDistribution / 5) * 5;
        }
      }
      if (yLeft) {
        yLeft.display = this.showCumulative;
      }
    }

    this.chart.update();
  }

  // Calculate percentage for interval [min, max] using linear interpolation on raw cumulative data
  calculateIntervalPercentage(): void {
    if (!this.data?.rawCumulGraph || this.data.rawCumulGraph.length === 0) return;

    const effectiveMin = Math.min(this.intervalMin, this.intervalMax);
    const effectiveMax = Math.max(this.intervalMin, this.intervalMax);

    const minPercent = this.interpolateCumulPercent(effectiveMin);
    const maxPercent = this.interpolateCumulPercent(effectiveMax);

    this.intervalPercentage = Math.max(0, Math.round((maxPercent - minPercent) * 100) / 100);
  }

  private interpolateCumulPercent(size: number): number {
    const graph = this.data.rawCumulGraph;
    if (graph.length === 0) return 0;

    if (size <= graph[0][0]) return graph[0][1];
    if (size >= graph[graph.length - 1][0]) return graph[graph.length - 1][1];

    for (let i = 1; i < graph.length; i++) {
      if (graph[i][0] >= size) {
        const x0 = graph[i - 1][0];
        const y0 = graph[i - 1][1];
        const x1 = graph[i][0];
        const y1 = graph[i][1];
        return y0 + (size - x0) / (x1 - x0) * (y1 - y0);
      }
    }
    return graph[graph.length - 1][1];
  }

  // Calculate diameter from percentage using linear interpolation on raw cumulative data
  calculateDiameterFromPercent(index: number): void {
    if (!this.data?.rawCumulGraph || this.data.rawCumulGraph.length === 0) return;

    let targetPercent: number;
    switch (index) {
      case 1: targetPercent = this.percentD1; break;
      case 2: targetPercent = this.percentD2; break;
      case 3: targetPercent = this.percentD3; break;
      default: return;
    }

    targetPercent = Math.max(0, Math.min(100, targetPercent));

    const graph = this.data.rawCumulGraph;
    let diameter = graph[graph.length - 1][0];
    for (let i = 1; i < graph.length; i++) {
      if (graph[i][1] >= targetPercent) {
        const x0 = graph[i - 1][0];
        const y0 = graph[i - 1][1];
        const x1 = graph[i][0];
        const y1 = graph[i][1];
        const ratio = (targetPercent - y0) / (y1 - y0);
        diameter = Math.round(x0 + ratio * (x1 - x0));
        break;
      }
    }

    switch (index) {
      case 1: this.diameterD1 = diameter; break;
      case 2: this.diameterD2 = diameter; break;
      case 3: this.diameterD3 = diameter; break;
    }
  }

  private async getFragData(): Promise<GraphData> {
    return this.blastService.getFragmentationData(this.blastId());
  }

  loadMuckpileImage(): void {
    if (this.currentImageUrl) {
      window.URL.revokeObjectURL(this.currentImageUrl);
      this.currentImageUrl = null;
    }
    this.imagePath = null;

    this.blastService.getMuckpileImage(this.blastId()).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        this.currentImageUrl = url;
        this.imagePath = this.sanitizer.bypassSecurityTrustUrl(url);
        this.cdr.markForCheck();
      },
      error: () => {
        this.imagePath = null;
        this.cdr.markForCheck();
      }
    });
  }

  openImageModal(): void {
    if (!this.imagePath) return;
    this.dialog.open(ImageModalComponent, {
      data: { imagePath: this.imagePath }
    });
  }

  private async initChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    const ctx = document.getElementById('fragChart') as HTMLCanvasElement;
    if (!ctx) return;

    let data: GraphData = null;
    try {
      data = await this.getFragData();
      this.data = data;

      if (data) {
        this.calculateDiameterFromPercent(1);
        this.calculateDiameterFromPercent(2);
        this.calculateDiameterFromPercent(3);
        this.calculateIntervalPercentage();
      }
    } catch {
      this.data = null;
    }

    const defaultLabels = [1, 10, 100, 1000];
    const sizes = data?.sizes?.length > 0 ? data.sizes : defaultLabels;
    const percentData = data?.percent || [];
    const cumulPercentData = data?.cumulPercent || [];
    const maxDistribution = percentData.length > 0 ? Math.ceil(Math.max(...percentData) / 5) * 5 : 25;

    // Filter out zero sizes (log scale can't handle 0) and build {x, y} points
    const distributionPoints: {x: number, y: number}[] = [];
    const cumulativePoints: {x: number, y: number}[] = [];
    for (let i = 0; i < sizes.length; i++) {
      if (sizes[i] > 0) {
        distributionPoints.push({ x: sizes[i], y: percentData[i] || 0 });
        cumulativePoints.push({ x: sizes[i], y: cumulPercentData[i] || 0 });
      }
    }

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        datasets: [
          {
            label: this.translate.instant('GLOBAL.FRAGMENTATION_DISTRIBUTION'),
            data: distributionPoints,
            borderColor: CHART_COLORS.amarante,
            backgroundColor: CHART_COLORS.amarante,
            borderWidth: 1,
            barPercentage: 1,
            categoryPercentage: 1,
            yAxisID: 'yRight',
            order: 2
          },
          {
            label: this.translate.instant('GLOBAL.FRAGMENTATION_CUMULATIVE'),
            type: 'line',
            data: cumulativePoints,
            borderColor: CHART_COLORS.amarante,
            backgroundColor: CHART_COLORS.amaranteLight,
            fill: true,
            pointRadius: 0,
            borderWidth: 2,
            yAxisID: 'yLeft',
            order: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              title: function (items) {
                if (items.length > 0) {
                  return Math.round(items[0].parsed.x) + ' mm';
                }
                return '';
              },
              label: function (context) {
                const value = Math.round(context.parsed.y * 100) / 100;
                return `${context.dataset.label}: ${value}%`;
              }
            }
          }
        },
        scales: {
          yLeft: {
            type: 'linear' as const,
            display: true,
            position: 'left' as const,
            title: {
              display: true,
              text: this.translate.instant('GLOBAL.FRAGMENTATION_CUMULATIVE')
            },
            ticks: {
              stepSize: 10,
              autoSkip: false,
              callback: function (value) {
                return value + '%';
              },
            },
            min: 0,
            max: 100,
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)'
            }
          },
          yRight: {
            type: 'linear' as const,
            display: true,
            position: 'right' as const,
            title: {
              display: true,
              text: this.translate.instant('GLOBAL.FRAGMENTATION_DISTRIBUTION')
            },
            ticks: {
              stepSize: 5,
              autoSkip: false,
              callback: function (value) {
                return value + '%';
              },
            },
            min: 0,
            max: maxDistribution,
            grid: {
              drawOnChartArea: false
            }
          },
          x: {
            type: 'logarithmic' as const,
            title: {
              display: true,
              text: this.translate.instant('GLOBAL.DIAMETER_MM')
            },
            ticks: {
              callback: function (value) {
                const num = Number(value);
                if ([1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000].includes(num)) {
                  return num;
                }
                return '';
              },
              autoSkip: false,
              maxRotation: 0
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)'
            }
          }
        }
      },
      plugins: []
    });

    this.updateChartVisibility();
  }
}
